import { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { PdfToolbar } from "./components/PdfToolbar";
import { PdfThumbnails } from "./components/PdfThumbnails";
import { PdfInfiniteScroller, PdfInfiniteScrollerRef } from "./PdfInfiniteScroller";
import { PdfLoadingSkeleton } from "./components/PdfLoadingSkeleton";
import { usePdfLoader } from "./hooks/usePdfLoader";
import { usePdfTextIndex } from "./hooks/usePdfTextIndex";
import { usePdfSearchWithRects } from "./hooks/usePdfSearchWithRects";
import { ScrollAnchor } from "./hooks/usePageVisibilityObserver";
import { usePdfSelection } from "./selection/usePdfSelection";
import { PdfFloatingMenu } from "./selection/PdfFloatingMenu";
import { usePdfAnnotations } from "./annotations/usePdfAnnotations";
import { PdfInlinePopover } from "./annotations/PdfInlinePopover";
import { expandScrollContext } from "./context/expandScrollContext";
import { usePdfThumbnailUpload } from "@/hooks/usePdfThumbnailUpload";
import { useSaveDocumentText, useTextExtractionStatus } from "@/hooks/useDocumentText";
import { useGenerateDocumentChapters } from "@/hooks/useDocumentChapters";
import { useContentNavigationOptional } from "@/context/ContentNavigationContext";
import type { PdfAnnotation } from "./annotations/types";
import "./pdf.css";

export type FitMode = "page-width" | "page-fit" | "actual" | "custom";

interface PdfViewerProps {
  storagePath: string;
  contentId: string;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export function PdfViewer({ storagePath, contentId }: PdfViewerProps) {
  const { user } = useAuth();
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loadingUrl, setLoadingUrl] = useState(true);

  // Viewer UI State
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1.0);
  const [fitMode, setFitMode] = useState<FitMode>("page-width");
  const [rotation, setRotation] = useState(0);
  const [thumbnailsOpen, setThumbnailsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<PdfInfiniteScrollerRef>(null);
  const [viewerSize, setViewerSize] = useState({ width: 0, height: 0 });
  
  // Scroll anchoring for stable scaling
  const scrollAnchorRef = useRef<ScrollAnchor | null>(null);
  const prevZoomRef = useRef(zoom);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Selection & Annotations
  const { selection, clearSelection } = usePdfSelection();
  const { annotations, addAnnotation, removeAnnotation } = usePdfAnnotations(
    contentId,
    user?.id ?? null
  );
  const [openAnnotation, setOpenAnnotation] = useState<PdfAnnotation | null>(null);
  const [openAnchorRect, setOpenAnchorRect] = useState<DOMRect | null>(null);
  const [isExplaining, setIsExplaining] = useState(false);

  // Thumbnail generation
  const { generateAndUploadThumbnail } = usePdfThumbnailUpload();
  const thumbnailGeneratedRef = useRef(false);

  // Text extraction for document chapters
  const saveDocumentText = useSaveDocumentText();
  const generateChapters = useGenerateDocumentChapters();
  const { data: textExtractionStatus } = useTextExtractionStatus(contentId);
  const textExtractionDoneRef = useRef(false);

  // Navigation context for cross-panel communication
  const navigationContext = useContentNavigationOptional();

  // Fetch signed URL for the private PDF
  useEffect(() => {
    async function loadSigned() {
      setLoadingUrl(true);
      const { data, error } = await supabase.storage
        .from("content-uploads")
        .createSignedUrl(storagePath, 3600);

      if (error) {
        console.error("Signed URL error:", error);
        setSignedUrl(null);
      } else {
        setSignedUrl(data.signedUrl);
      }
      setLoadingUrl(false);
    }

    loadSigned();
  }, [storagePath]);

  // Load PDF.js document from signed URL
  const { pdf, loading: loadingPdf, error, numPages, pageSize } =
    usePdfLoader(signedUrl || "");

  // Text index for search (with bounding boxes)
  const { pages: textIndex } = usePdfTextIndex(pdf);

  // Generate thumbnail on first PDF load if not already generated
  useEffect(() => {
    if (!pdf || !contentId || thumbnailGeneratedRef.current) return;

    // Check if thumbnail already exists
    async function checkAndGenerateThumbnail() {
      const { data: content } = await supabase
        .from("content")
        .select("thumbnail_url")
        .eq("id", contentId)
        .maybeSingle();

      // Only generate if no thumbnail exists
      if (!content?.thumbnail_url) {
        thumbnailGeneratedRef.current = true;
        await generateAndUploadThumbnail(pdf, contentId);
      } else {
        thumbnailGeneratedRef.current = true;
      }
    }

    checkAndGenerateThumbnail();
  }, [pdf, contentId, generateAndUploadThumbnail]);

  // Extract and save document text for chapter generation on first load
  useEffect(() => {
    if (!pdf || !contentId || textExtractionDoneRef.current) return;
    if (textIndex.length === 0) return; // Wait for text extraction to complete
    
    // Skip if already extracted or in progress
    if (textExtractionStatus === 'ready' || textExtractionStatus === 'processing') {
      textExtractionDoneRef.current = true;
      return;
    }

    async function extractAndSaveText() {
      textExtractionDoneRef.current = true;
      
      console.log(`[PdfViewer] Saving extracted text for ${textIndex.length} pages`);
      
      try {
        // Convert textIndex to the format expected by saveDocumentText
        const pages = textIndex.map(page => ({
          pageNumber: page.pageNumber,
          text: page.fullText,
          wordCount: page.fullText.split(/\s+/).filter(Boolean).length,
        }));

        // Save to database
        await saveDocumentText.mutateAsync({ contentId, pages });
        
        console.log(`[PdfViewer] Text saved successfully, triggering chapter generation`);

        // Trigger chapter generation if we have enough pages
        if (pages.length >= 3) {
          const fullText = pages
            .map(p => `[Page ${p.pageNumber}]\n${p.text}`)
            .join("\n\n");

          generateChapters.mutate(
            { contentId, documentText: fullText },
            {
              onSuccess: (data) => {
                if (data?.chapterCount > 0) {
                  console.log(`[PdfViewer] Generated ${data.chapterCount} chapters`);
                }
              },
              onError: (error) => {
                console.error("[PdfViewer] Chapter generation failed:", error);
              },
            }
          );
        }
      } catch (error) {
        console.error("[PdfViewer] Failed to save document text:", error);
      }
    }

    extractAndSaveText();
  }, [pdf, contentId, textIndex, textExtractionStatus, saveDocumentText, generateChapters]);

  // Build page text index for context expansion (page number -> full text)
  const pageTextIndex = useMemo(() => {
    const index: Record<number, string> = {};
    for (const page of textIndex) {
      index[page.pageNumber] = page.fullText;
    }
    return index;
  }, [textIndex]);

  // Search functionality with rect-based matches
  const searchInputRef = useRef<HTMLInputElement>(null);
  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    matches: searchMatches,
    activeIndex: activeMatchIndex,
    isSearchOpen,
    toggleSearch,
    openSearch,
    nextMatch,
    prevMatch,
    currentMatch,
    resultCount: searchResultsCount,
  } = usePdfSearchWithRects(textIndex);

  // Scroll to current match when it changes and center it on screen
  useEffect(() => {
    if (currentMatch) {
      scrollerRef.current?.scrollToMatchRect(
        { pageNumber: currentMatch.pageNumber, rect: currentMatch.rect },
        zoom
      );
    }
  }, [currentMatch, zoom]);

  // Ctrl+F keyboard shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        openSearch();
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [openSearch]);

  // Measure viewer container with debounced resize handling
  useEffect(() => {
    if (!viewerRef.current) return;
    const el = viewerRef.current;

    const updateSize = () => {
      const rect = el.getBoundingClientRect();
      setViewerSize({ width: rect.width, height: rect.height });
    };

    const debouncedUpdateSize = () => {
      if (fitMode === "page-width" || fitMode === "page-fit") {
        scrollAnchorRef.current = scrollerRef.current?.captureScrollAnchor() ?? null;
      }
      
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = setTimeout(updateSize, 50);
    };

    updateSize();

    const observer = new ResizeObserver(() => debouncedUpdateSize());
    observer.observe(el);

    window.addEventListener("resize", debouncedUpdateSize);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", debouncedUpdateSize);
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
    };
  }, [fitMode]);

  // Recompute zoom when fit mode or container size changes
  useEffect(() => {
    if (!pageSize) return;
    if (!viewerSize.width || !viewerSize.height) return;

    let newZoom = zoom;
    
    if (fitMode === "page-width") {
      const padding = 48;
      const availableWidth = viewerSize.width - padding;
      newZoom = availableWidth / pageSize.width;
    } else if (fitMode === "page-fit") {
      const padding = 48;
      const verticalPadding = 48;
      const scaleW = (viewerSize.width - padding) / pageSize.width;
      const scaleH = (viewerSize.height - verticalPadding) / pageSize.height;
      newZoom = Math.min(scaleW, scaleH);
    } else if (fitMode === "actual") {
      newZoom = 1;
    }
    
    if (newZoom !== zoom) {
      setZoom(newZoom);
    }
  }, [fitMode, pageSize, viewerSize.width, viewerSize.height, zoom]);

  // Restore scroll anchor after zoom changes
  useLayoutEffect(() => {
    if (prevZoomRef.current !== zoom && scrollAnchorRef.current) {
      requestAnimationFrame(() => {
        if (scrollAnchorRef.current) {
          scrollerRef.current?.restoreScrollAnchor(scrollAnchorRef.current);
          scrollAnchorRef.current = null;
        }
      });
    }
    prevZoomRef.current = zoom;
  }, [zoom]);

  // Annotation action handlers
  const handleHighlight = useCallback(async () => {
    if (!selection) return;
    await addAnnotation({
      pageNumber: selection.pageNumber,
      text: selection.text,
      rects: selection.rects,
      type: "highlight",
      payload: {},
    });
    clearSelection();
  }, [selection, addAnnotation, clearSelection]);

  // Get current AI context (selection > scroll-aware fallback)
  const getCurrentContext = useCallback((): { kind: "selection" | "visible"; text: string; pageNumbers: number[] } | null => {
    // Priority 1: Explicit text selection
    if (selection) {
      return {
        kind: "selection",
        text: selection.text,
        pageNumbers: [selection.pageNumber],
      };
    }

    // Priority 2: Scroll-aware context from visible position
    const anchor = scrollerRef.current?.captureScrollAnchor();
    if (anchor && Object.keys(pageTextIndex).length > 0) {
      return expandScrollContext(anchor, pageTextIndex);
    }

    return null;
  }, [selection, pageTextIndex]);

  const handleExplain = useCallback(async () => {
    const context = getCurrentContext();
    if (!context) {
      toast.error("No content available to explain");
      return;
    }
    
    setIsExplaining(true);
    toast.info("Getting AI explanation...");
    
    try {
      const { data, error } = await supabase.functions.invoke('explain-pdf-text', {
        body: {
          text: context.text,
          pageNumber: context.pageNumbers[0],
          pageNumbers: context.pageNumbers,
          contextKind: context.kind,
        },
      });

      if (error) {
        console.error('Explain error:', error);
        toast.error("Failed to get AI explanation");
        setIsExplaining(false);
        return;
      }

      const aiResponse = data?.explanation || "Unable to generate explanation";

      // Only create annotation if we have a selection with rects
      if (selection) {
        await addAnnotation({
          pageNumber: selection.pageNumber,
          text: selection.text,
          rects: selection.rects,
          type: "explain",
          payload: { aiResponse },
        });
        clearSelection();
      }
      
      toast.success("Explanation ready!");
    } catch (err) {
      console.error('Explain error:', err);
      toast.error("Failed to get AI explanation");
    } finally {
      setIsExplaining(false);
    }
  }, [getCurrentContext, selection, addAnnotation, clearSelection]);

  const handleNote = useCallback(async () => {
    if (!selection) return;
    const noteText = prompt("Add a note:") ?? "";
    if (!noteText.trim()) return;

    await addAnnotation({
      pageNumber: selection.pageNumber,
      text: selection.text,
      rects: selection.rects,
      type: "note",
      payload: { noteText },
    });
    clearSelection();
  }, [selection, addAnnotation, clearSelection]);

  const handleAsk = useCallback(async () => {
    if (!selection) return;
    const question = prompt("Ask about this text:") ?? "";
    if (!question.trim()) return;

    await addAnnotation({
      pageNumber: selection.pageNumber,
      text: selection.text,
      rects: selection.rects,
      type: "note",
      payload: { question },
    });
    clearSelection();
  }, [selection, addAnnotation, clearSelection]);

  const handleOpenAnnotation = useCallback((annotation: PdfAnnotation, anchorRect: DOMRect, pageNumber: number) => {
    setOpenAnnotation(annotation);
    setOpenAnchorRect(anchorRect);
  }, []);

  const handleZoomChange = useCallback((nextZoom: number) => {
    const clamped = clamp(nextZoom, 0.4, 4);
    setFitMode("custom");
    setZoom(clamped);
  }, []);

  const handleFitModeChange = useCallback((mode: FitMode) => {
    setFitMode(mode);
  }, []);

  const handleRotate = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  const handleDownload = useCallback(() => {
    if (!signedUrl) return;
    const link = document.createElement("a");
    link.href = signedUrl;
    link.download = storagePath.split("/").pop() || "document.pdf";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [signedUrl, storagePath]);

  const handleToggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current.requestFullscreen();
    }
  }, []);

  const scrollToPage = useCallback((page: number) => {
    scrollerRef.current?.scrollToPage(page);
  }, []);

  // Register navigation function with context for cross-panel chapter navigation
  useEffect(() => {
    if (navigationContext) {
      navigationContext.registerPdfNavigation(scrollToPage);
      return () => navigationContext.registerPdfNavigation(null);
    }
  }, [navigationContext, scrollToPage]);

  const handlePageChangeFromScroll = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  if (loadingUrl || loadingPdf) return <PdfLoadingSkeleton />;

  if (error || !pdf) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        Failed to load PDF.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full flex-col bg-background"
    >
      {/* Toolbar */}
      <PdfToolbar
        currentPage={currentPage}
        numPages={numPages}
        onPageChange={scrollToPage}
        zoom={zoom}
        onZoomChange={handleZoomChange}
        fitMode={fitMode}
        onFitModeChange={handleFitModeChange}
        thumbnailsOpen={thumbnailsOpen}
        onToggleThumbnails={() => setThumbnailsOpen((prev) => !prev)}
        rotation={rotation}
        onRotate={handleRotate}
        onDownload={handleDownload}
        darkMode={darkMode}
        onToggleTheme={() => setDarkMode((prev) => !prev)}
        onToggleFullscreen={handleToggleFullscreen}
        isSearchOpen={isSearchOpen}
        onToggleSearch={toggleSearch}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchResultsCount={searchResultsCount}
        activeMatchIndex={activeMatchIndex}
        onNextMatch={nextMatch}
        onPrevMatch={prevMatch}
        searchInputRef={searchInputRef}
        containerRef={containerRef}
      />

      <div className="flex flex-grow overflow-hidden">
        {/* Left thumbnails */}
        {thumbnailsOpen && (
          <PdfThumbnails
            pdf={pdf}
            numPages={numPages}
            currentPage={currentPage}
            onPageSelect={scrollToPage}
            darkMode={darkMode}
          />
        )}

        {/* Main viewer with infinite scroll */}
        <div
          ref={viewerRef}
          className="relative flex-grow overflow-hidden"
        >
          <PdfInfiniteScroller
            ref={scrollerRef}
            pdf={pdf}
            numPages={numPages}
            zoom={zoom}
            rotation={rotation}
            darkMode={darkMode}
            pageSize={pageSize}
            onPageChange={handlePageChangeFromScroll}
            annotations={annotations}
            onAnnotationClick={handleOpenAnnotation}
            searchMatches={searchMatches}
            activeSearchMatch={currentMatch}
          />
        </div>
      </div>

      {/* Floating menu on text selection */}
      {selection && (
        <PdfFloatingMenu
          selection={selection}
          onExplain={handleExplain}
          onAsk={handleAsk}
          onNote={handleNote}
          onHighlight={handleHighlight}
          onClose={clearSelection}
        />
      )}

      {/* Annotation popover */}
      {openAnnotation && openAnchorRect && (
        <PdfInlinePopover
          annotation={openAnnotation}
          anchorClientRect={openAnchorRect}
          onClose={() => setOpenAnnotation(null)}
          onDelete={() => {
            removeAnnotation(openAnnotation.id);
            setOpenAnnotation(null);
          }}
        />
      )}
    </div>
  );
}
